<template>
  <v-app id="inspire" :class="frameClass">
    <!-- Süsteem -> Seaded -> Uued vaated -> Intra uus raam ja Töölaud: the menu
         each user places left, right or on top under their own name, or hides.
         Off, the frame below the v-else is the one intra has always had. -->
    <template v-if="frame">
      <!-- A phone has no room for a side or top menu: always the drawer behind
           a ☰, whatever the person chose for a bigger screen. -->
      <template v-if="mobile">
        <v-app-bar class="frame-top frame-chrome" height="56" flat>
          <button class="frame-icon-btn" type="button" aria-label="Menüü" @click.stop="mobileOpen = !mobileOpen">
            <v-icon>mdi-menu</v-icon>
          </button>
          <router-link to="/" class="frame-brand ml-2"><b>GASPRE</b><span>intra</span></router-link>
        </v-app-bar>
        <v-navigation-drawer v-model="mobileOpen" temporary location="left" width="280" class="frame-side">
          <frame-side-nav :items="items" mobile @navigate="mobileOpen = false" />
        </v-navigation-drawer>
      </template>
      <template v-else-if="!layout.hidden">
        <v-navigation-drawer
          v-if="layout.position !== 'top'"
          permanent
          :location="layout.position"
          width="256"
          class="frame-side"
        >
          <frame-side-nav :items="items" :location="layout.position" @hide="hideMenu" />
        </v-navigation-drawer>
        <v-app-bar v-else class="frame-top" height="58" flat>
          <frame-top-nav :items="items" />
          <button class="frame-icon-btn" type="button" aria-label="Peida menüü" title="Peida menüü" @click="hideMenu">
            <v-icon size="22">mdi-chevron-up</v-icon>
          </button>
          <profile-menu location="bottom end">
            <template #activator="{ props, open, initials, fullName }">
              <button class="frame-me-pill" type="button" aria-haspopup="dialog" v-bind="props">
                <span class="frame-avatar" aria-hidden="true">{{ initials }}</span>
                {{ fullName }}
                <v-icon size="18">{{ open ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </button>
            </template>
          </profile-menu>
        </v-app-bar>
      </template>
      <!-- A hidden menu leaves a strip of icons on the side it went from (a top
           menu's on the left). A drawer, so the page starts beside it rather
           than under a floating button. -->
      <v-navigation-drawer
        v-else
        permanent
        :location="layout.position === 'right' ? 'right' : 'left'"
        width="64"
        class="frame-side"
      >
        <frame-rail :items="items" :side="layout.position === 'right' ? 'right' : 'left'" @show="saveLayout({ hidden: false })" />
      </v-navigation-drawer>
    </template>
    <template v-else>
      <!-- The app bar comes before the drawer on purpose: that is how Vuetify 3
           says what Vuetify 2's `clipped-left` said — the bar spans the full width
           and the drawer starts underneath it. Below lg the drawer is a temporary
           one over the page in both majors, so clipping only ever mattered above. -->
      <v-app-bar color="blue-darken-3" theme="dark">
        <v-app-bar-nav-icon @click.stop="showMenu = !showMenu" />
        <v-toolbar-title class="ml-0 pl-4 mr-4">
          <span class="d-none d-md-inline">Gaspre Intra</span>
        </v-toolbar-title>
        <v-spacer />
      </v-app-bar>
      <v-navigation-drawer v-model="showMenu">
        <main-menu :items="items" />
        <template #append>
          <user-info />
        </template>
      </v-navigation-drawer>
    </template>
    <v-main>
      <v-container fluid>
        <router-view />
        <pwned-password-dialog />
        <v-snackbar v-model="notification.visible" location="top" :color="notification.color">
          {{ notification.text }}
          <template #actions>
            <v-btn color="white" variant="text" @click="hideNotification">
              Sulge
            </v-btn>
          </template>
        </v-snackbar>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import MainMenu from './../components/MainMenu.vue'
import UserInfo from './../components/UserInfo.vue'
import FrameSideNav from './../components/frame/FrameSideNav.vue'
import FrameRail from './../components/frame/FrameRail.vue'
import FrameTopNav from './../components/frame/FrameTopNav.vue'
import ProfileMenu from './../components/frame/ProfileMenu.vue'
import PwnedPasswordDialog from './../components/PwnedPasswordDialog.vue'
import api from './../api'
import loadArchivo from './../plugins/archivo'
import '@/styles/frame.css'

import { mapState, mapActions } from 'vuex'

export default {
  components: { MainMenu, UserInfo, FrameSideNav, FrameRail, FrameTopNav, ProfileMenu, PwnedPasswordDialog },
  data: () => ({
    showMenu: null,
    // The new frame's drawer on a phone.
    mobileOpen: false,
    // How many acts are waiting, on the Hooldusaktid entry of the new frame.
    aktBadge: 0
  }),
  computed: {
    ...mapState(['notification', 'features', 'layout']),
    frame () {
      return !!this.features.intraFrame
    },
    mobile () {
      return this.$vuetify.display.smAndDown
    },
    frameClass () {
      if (!this.frame) return null
      return ['frame', { 'frame--hidden': this.layout.hidden && !this.mobile }]
    },
    items () {
      const isAdminOrRoot = this.$hasRole(['admin', 'root'])
      const isRoot = this.$hasRole(['root'])
      const isSecretary = this.$hasRole(['secretary'])
      const isTechnic = this.$hasRole(['tehnic'])
      const hasAnyRole = isAdminOrRoot || isSecretary || isTechnic

      return [{
        icon: 'mdi-view-dashboard-outline',
        text: 'Töölaud',
        link: '/',
        model: this.$route.path.startsWith('/dashboard') || this.$route.path === '/',
      }, {
        icon: 'mdi-calendar-month',
        text: 'Kalender',
        hide: !hasAnyRole,
        model: this.$route.path.startsWith('/calendar'),
        children: [{
          text: 'Kalenderivaade',
          link: '/calendar/full'
        }, {
          text: 'Kõik sissekanded',
          link: '/calendar/events'
        }]
      }, {
        icon: 'mdi-account-tie',
        text: 'Kliendid',
        // Technicians see the list too (2026-10-14), and only the list.
        hide: !(isAdminOrRoot || isSecretary || isTechnic),
        model: this.$route.path.startsWith('/client'),
        children: [{
          text: 'Kõik kliendid',
          link: { name: 'client/list', params: { type: 'all' } }
        }, {
          text: 'Erakliendid',
          link: { name: 'client/list', params: { type: 'private' } },
          hide: !(isAdminOrRoot || isSecretary)
        }, {
          text: 'Firmad',
          link: { name: 'client/list', params: { type: 'company' } },
          hide: !(isAdminOrRoot || isSecretary)
        }, {
          text: 'Otsi duplikaate',
          link: { name: 'client/duplicates' },
          hide: !isAdminOrRoot
        }, {
          text: 'Lisa uus klient',
          link: { name: 'client/create' },
          hide: !(isAdminOrRoot || isSecretary)
        }]
      }, {
        icon: 'mdi-warehouse',
        text: 'Objektid',
        hide: !hasAnyRole,
        model: this.$route.path.startsWith('/object'),
        children: [{
          text: 'Kõik objektid',
          link: { name: 'object/list', params: { type: 'all' } }
        }, {
          text: 'Tavalised',
          link: { name: 'object/list', params: { type: 'regular' } }
        }, {
          text: 'Lepingulised',
          link: { name: 'object/list', params: { type: 'contract' } }
        }, {
          text: 'Otsi duplikaate',
          link: { name: 'object/duplicates' },
          hide: !isAdminOrRoot,
        }, {
          text: 'Lisa uus objekt',
          link: { name: 'object/create' },
          hide: !(isAdminOrRoot || isSecretary),
        }]
      }, {
        icon: 'mdi-sitemap',
        text: 'Planeerija',
        hide: !hasAnyRole,
        link: '/planner'
      }, {
        // The akt module is deployment-optional (Süsteem -> Seaded), so the
        // entry only exists where it leads somewhere. /undone rather than the
        // akt app's root: a secretary may read Tegemata aktid, but Tuleviku aktid
        // answers them 403.
        icon: 'mdi-clipboard-text-outline',
        text: 'Hooldusaktid',
        href: '/akt/undone',
        hide: !this.features.akt || !hasAnyRole,
        badge: this.aktBadge || null,
        badgeTitle: 'Tegemata aktid viimase 90 päeva hooldustest'
      }, {
        icon: 'mdi-account-group',
        text: 'Kasutajad',
        // The office's own pages: below a rule in the side menu, under one
        // "Haldus" menu on top.
        section: 'admin',
        hide: !isAdminOrRoot,
        model: this.$route.path.startsWith('/users'),
        children: [
          { text: 'Kõik kasutajad', link: { name: 'user/list' } },
          { text: 'Lisa uus kasutaja', link: { name: 'user/create' }}
        ],
      }, {
        icon: 'mdi-server-network',
        text: 'Süsteem',
        section: 'admin',
        hide: !isRoot,
        model: this.$route.path.startsWith('/system'),
        children: [
          { text: 'Seaded', link: { name: 'system/settings' } },
          { text: 'Varundus', link: { name: 'system/backup' } },
          { text: 'Meeldetuletused', link: { name: 'system/reminders' } },
          { text: 'SMS', link: { name: 'system/sms' } },
          { text: 'E-post', link: { name: 'system/mail' } },
          { text: 'Statistika', link: { name: 'system/stats' } },
          // A group inside the group: room for staff next to customers.
          { text: 'Turvalisus', children: [{ text: 'Kliendid', link: { name: 'system/security/clients' } }, { text: 'Pääsuvõtmed', link: { name: 'system/security/passkeys' } }, { text: 'Lekkinud paroolid', link: { name: 'system/security/passwords' } }] },
          { text: 'Andmete korrastamine', children: [
            { text: 'Linnad', link: { name: 'system/data/cities' } },
            { text: 'Riigid', link: { name: 'system/data/countries' } },
            { text: 'Maksmata tööd', link: { name: 'system/data/unpaid' } }
          ] }
        ]
      }]
      .map(item => {
        const visible = children => children.filter(({ hide }) => !hide).map(child =>
          child.children ? { ...child, children: visible(child.children) } : child)
        item.children = item.children ? visible(item.children) : item.children
        return item
      })
      .filter(({ hide }) => !hide)
    }
  },
  watch: {
    // Turned on in Seaded or found on at load: the frame needs its typeface,
    // this person's layout and the act count before it draws as it should.
    frame: {
      handler (on) {
        if (!on) return
        loadArchivo()
        this.getLayout()
        this.loadAktBadge()
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions(['hideNotification', 'getFeatures', 'getLayout', 'saveLayout', 'getPrefs']),
    hideMenu () {
      this.saveLayout({ hidden: true })
    },
    // The same count as Töölaud's "Tegemata aktid" card (the akt module's own
    // 90-day window). Quiet: no module, no role or no answer is no badge.
    async loadAktBadge () {
      if (!this.features.akt || !this.$hasRole(['tehnic', 'admin', 'secretary', 'root'])) return
      try {
        const visits = await api.getUndoneAkts({ part: 'visits', days: 90, limit: 1 })
        this.aktBadge = (visits && visits.total) || 0
      } catch (err) {
        this.aktBadge = 0
      }
    }
  },
  mounted () {
    this.getFeatures()
    this.getPrefs()
  }
}
</script>